import { Content, InfoCard, Link, MarkdownContent, Page } from '@backstage/core-components';
import {
  HomePageStarredEntities,
  HomePageRecentlyVisited,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Button, CardActions, CardContent, Grid, Typography, makeStyles } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';

type PlatformLink = {
  label: string;
  url: string;
};

type PlatformCard = {
  name: string;
  description: string;
  links: PlatformLink[];
};

type NewsItem = {
  id: string;
  title: string;
  publishedAt: string;
  summary: string;
  markdownPath: string;
};

const platformCards: PlatformCard[] = [
  {
    name: 'Nexus',
    description: 'Event streaming platform for publishing and consuming data products.',
    links: [
      { label: 'Overview', url: 'https://pandoradigital.atlassian.net/wiki/spaces/NPSGP/pages/5361926580/About+Nexus+Platform' },
      { label: 'Onboarding', url: 'https://pandoradigital.atlassian.net/wiki/spaces/NPSGP/pages/4769317086/Platform+Onboarding' },
      { label: 'Support', url: 'https://pandoradigital.atlassian.net/wiki/spaces/NPSGP/pages/4660363293/Support+Request+Process+Management' },
      { label: 'Quick start', url: 'https://pandoradigital.atlassian.net/wiki/spaces/NPSGP/pages/4769317006/Quick+start' },
    ],
  },
  {
    name: 'Olympus',
    description: 'Data and analytics platform for product insights and reporting workflows.',
    links: [
      { label: 'Overview', url: 'https://pandoradigital.atlassian.net/wiki/spaces/O/overview' },
      { label: 'Onboarding', url: 'https://pandoradigital.atlassian.net/wiki/spaces/O/pages/2948268220/Data+Workspace+Onboarding+Upskilling' },
    ],
  },
  {
    name: 'PONG',
    description: 'API platform for designing, publishing, and operating product APIs.',
    links: [
      { label: 'Overview', url: 'https://pandoradigital.atlassian.net/wiki/spaces/KONG/overview' },
      { label: 'Onboarding', url: 'https://teams.microsoft.com/l/channel/19%3atZRrDaiLw7faTCQbTRognTVu8jwm581mszpsrbcpbQg1%40thread.tacv2/General?groupId=8f8550b7-89ca-4f05-ae8a-75fd96b81d04&tenantId=656793e6-d51d-4bb2-b5fa-c66ddd181a40' },
    ],
  },
  {
    name: 'PAKS',
    description: 'Kubernetes platform for hosting workloads with paved-path operations.',
    links: [
      { label: 'Overview', url: 'https://pandoradigital.atlassian.net/wiki/spaces/IDP/pages/3878912857/PAKS+Kubernetes+Platform' },
      { label: 'Onboarding', url: 'https://pandoradigital.atlassian.net/wiki/spaces/IDP/pages/4725670598/PAKS+-+Onboarding' },
    ],
  },
];

const newsItems: NewsItem[] = [
  {
    id: 'nexus-golden-path',
    title: 'Nexus Golden Path released for event onboarding',
    publishedAt: '2026-03-14',
    summary: 'New onboarding guide and templates reduce setup time for event producers.',
    markdownPath: '/home/news/2026-03-14-nexus-golden-path.md',
  },
  {
    id: 'olympus-self-service',
    title: 'Olympus self-service analytics onboarding available',
    publishedAt: '2026-03-10',
    summary: 'Teams can now bootstrap analytics workspaces with fewer manual approvals.',
    markdownPath: '/home/news/2026-03-10-olympus-self-service.md',
  },
  {
    id: 'paks-runtime-updates',
    title: 'PAKS runtime baseline updated',
    publishedAt: '2026-03-03',
    summary: 'Updated runtime baseline and migration checklist are now published for product teams.',
    markdownPath: '/home/news/2026-03-03-paks-runtime-baseline.md',
  },
];

const useStyles = makeStyles(theme => ({
  heroImage: {
    //width: '100%',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  heroDescription: {
    marginBottom: theme.spacing(2),
  },
  quickActionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  platformDescription: {
    marginBottom: theme.spacing(1),
  },
  platformLinks: {
    display: 'grid',
    rowGap: theme.spacing(0.5),
  },
  newsList: {
    display: 'grid',
    rowGap: theme.spacing(1),
  },
  newsButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
  },
  cardSectionTitle: {
    marginBottom: theme.spacing(1),
  },
}));

export const CustomizableHomePage = () => {
  const classes = useStyles();
  const [activeNewsId, setActiveNewsId] = useState(newsItems[0].id);
  const [newsContent, setNewsContent] = useState<Record<string, string>>({});

  const activeNews = useMemo(
    () => newsItems.find(item => item.id === activeNewsId) ?? newsItems[0],
    [activeNewsId],
  );

  useEffect(() => {
    let mounted = true;

    const loadNews = async () => {
      const entries = await Promise.all(
        newsItems.map(async item => {
          try {
            const response = await fetch(item.markdownPath);
            if (!response.ok) {
              return [item.id, 'News content is temporarily unavailable.'] as const;
            }

            const markdown = await response.text();
            return [item.id, markdown] as const;
          } catch {
            return [item.id, 'News content is temporarily unavailable.'] as const;
          }
        }),
      );

      if (mounted) {
        setNewsContent(Object.fromEntries(entries));
      }
    };

    loadNews();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InfoCard title="Welcome to Helios">
                    <img src="/homepage-topbanner.jpg" alt="Helios banner" className={classes.heroImage} />
                    <Typography variant="body1" className={classes.heroDescription}>
                      Use Helios to discover platform capabilities, onboard faster, and keep up with releases across Nexus, Olympus, Pong, and PAKS.
                    </Typography>
                    <HomePageSearchBar />
                  </InfoCard>
                </Grid>

                <Grid item xs={12}>
                  <InfoCard title="Platform launchpad">
                    <Grid container spacing={2}>
                      {platformCards.map(platform => (
                        <Grid item xs={12} sm={6} key={platform.name}>
                          <InfoCard title={platform.name}>
                            <Typography variant="body2" className={classes.platformDescription}>
                              {platform.description}
                            </Typography>
                            <div className={classes.platformLinks}>
                              {platform.links.map(link => (
                                <Link key={link.url} to={link.url} target="_blank" rel="noopener noreferrer">
                                  {link.label}
                                </Link>
                              ))}
                            </div>
                          </InfoCard>
                        </Grid>
                      ))}
                    </Grid>
                  </InfoCard>
                </Grid>
                {/* Let's hide the news section for now until we are "real" stories to tell */}
                {false && <Grid item xs={12}>
                  <InfoCard title="News and releases">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" className={classes.cardSectionTitle}>Latest updates</Typography>
                        <div className={classes.newsList}>
                          {newsItems.map(item => (
                            <Button
                              key={item.id}
                              className={classes.newsButton}
                              variant={activeNewsId === item.id ? 'contained' : 'outlined'}
                              color="primary"
                              onClick={() => setActiveNewsId(item.id)}
                            >
                              {item.title}
                            </Button>
                          ))}
                        </div>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Typography variant="h6">{activeNews.title}</Typography>
                        <Typography variant="caption" color="textSecondary">Published {activeNews.publishedAt}</Typography>
                        <CardContent>
                          <Typography variant="body2" paragraph>{activeNews.summary}</Typography>
                          <MarkdownContent content={newsContent[activeNews.id] ?? 'Loading news content...'} />
                        </CardContent>
                      </Grid>
                    </Grid>
                  </InfoCard>
                </Grid>}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <HomePageStarredEntities />
                </Grid>
                <Grid item xs={12}>
                  <HomePageRecentlyVisited />
                </Grid>
                <Grid item xs={12}>
                  <InfoCard title="Need help?">
                    <Typography variant="body2" paragraph>
                      Start with Helios documentation and support channels.
                    </Typography>
                    <div className={classes.platformLinks}>
                      <Link to="https://pandoradigital.atlassian.net/wiki/spaces/IDP/overview" target="_blank" rel="noopener noreferrer">
                        Helios Confluence Space
                      </Link>
                      <Link to="/catalog?filters[kind]=system">Explore Platform Systems</Link>
                      <Link to="/create">Start a new onboarding template</Link>
                    </div>
                  </InfoCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};